"use client";
import { RichText } from "@/baseComponents";
import { RichTextAlignment } from "@/types/enums";
import { GetUmbracoBaseUrl } from "@/utils/urlHelpers";
import { chakra, Heading, TextProps, VStack } from "@chakra-ui/react";
import DOMPurify from "isomorphic-dompurify";
import styles from "./richTextComponent.module.scss";

export interface RichTextProps {
  title?: string;
  subTitle?: string;
  markup?: string;
  alignment?: RichTextAlignment;
  titleProps?: TextProps;
  subTitleProps?: TextProps;
}

export default chakra(RichTextComponent);
function RichTextComponent({
  title,
  subTitle,
  markup,
  alignment = RichTextAlignment.Left,
  titleProps,
  subTitleProps,
  ...props
}: RichTextProps) {
  const umbracoBaseUrl = GetUmbracoBaseUrl();
  const componentTextAlignment =
    alignment === RichTextAlignment.Left
      ? "left"
      : alignment === RichTextAlignment.Center
        ? "center"
        : "right";
  return (
    <VStack spacing={6} alignItems={componentTextAlignment} {...props}>
      {title && (
        <Heading
          as={"h2"}
          fontSize={{ base: "2xl", md: "2rem", lg: "5xl" }}
          textAlign={componentTextAlignment}
          fontWeight={400}
          color={"brand.aubergine"}
          className={styles.title}
          {...titleProps}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(title.replace(/<\/p>/g, "</br>"), {
              ALLOWED_TAGS: ["strong", "br"],
            }),
          }}
        />
      )}
      {subTitle && (
        <Heading
          as={"h3"}
          fontSize={"2rem"}
          textAlign={componentTextAlignment}
          fontWeight={400}
          color={"brand.aubergine"}
          className={styles.title}
          {...subTitleProps}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(subTitle, { ALLOWED_TAGS: ["strong"] }),
          }}
        />
      )}
      {markup && (
        <RichText
          className={styles.rte}
          html={DOMPurify.sanitize(
            markup
              // First replacement: Process image tags with proper URL prefixing and cropping
              .replace(
                /<img\s+([^>]*?)src="(.*?)"([^>]*?)>/g,
                (match, beforeSrc, src, afterSrc) => {
                  const prefix = GetUmbracoBaseUrl();

                  // Extract width and height attributes
                  const widthMatch = afterSrc.match(/width="(\d+)"/);
                  const heightMatch = afterSrc.match(/height="(\d+)"/);
                  const width = widthMatch ? widthMatch[1] : "auto";
                  const height = heightMatch ? heightMatch[1] : "auto";

                  // Construct the cropped src
                  const cropParams = `?crop=center&width=${width}&height=${height}`;
                  const newSrc = `${prefix}${src}${cropParams}`;

                  // Update the src
                  return match.replace(src, newSrc);
                },
              )
              // Second replacement: Process links to ensure they have proper URLs
              .replace(
                /<a\s+([^>]*?)href="([^"]+)"([^>]*?)>/g,
                (match, beforeHref, href, afterHref) => {
                  let newHref = href;
                  // Only process relative URLs (not starting with http/https or mailto:)
                  if (
                    !href.startsWith("http") &&
                    !href.startsWith("mailto:") &&
                    !href.startsWith("tel:")
                  ) {
                    // Add base URL if it doesn't exist
                    if (
                      !href.startsWith(umbracoBaseUrl) &&
                      href.startsWith("/media/")
                    ) {
                      newHref = `${umbracoBaseUrl}${href.startsWith("/") ? "" : "/"}${href}`;
                    }
                  }
                  return `<a ${beforeHref}href="${newHref}"${afterHref}>`;
                },
              )
              .replace(/<\/p>/g, "</br>"),
            {
              ADD_ATTR: ["target"], // Allow target attribute in links
            },
          )}
        />
      )}
    </VStack>
  );
}
